import React, { Component } from "react";
import { APIClient } from "../../utils/APIClient";
import { API_URL } from "../../settings/local_settings";
import { AgencyHeader } from "./AgencyHeader";
import { AgencyTabs } from "./AgencyTabs";
import { Spinner } from "../Spinner";
import { withTranslation } from 'react-i18next';
import { BillingHistorySection } from "./subscription/BillingHistorySection";
import PlanChangeSection from "./subscription/PlanChangeSection";
import CheckoutForm from "./subscription/CheckoutForm";
import PendingPaymentBanner from "./subscription/PendingPaymentBanner";
import { CancelSubscriptionSection } from "./subscription/CancelSubscriptionSection";

class AgencySubscriptionContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            subscriptionData: null,
            billingData: null,
            billingLoading: true,
            isSuperAdmin: false,
            checkoutData: null,  // null = no checkout, objeto = checkout activo
            pendingPaymentUrl: null,
            commentSent: false,
        };
    }

    componentDidMount() {
        this.loadSubscriptionData();
        this.loadBillingHistory();
        this.fetchSuperAdminFlag();
    }

    fetchSuperAdminFlag() {
        new APIClient(API_URL + 'get-logged-user-data/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({ isSuperAdmin: data.is_super_admin === true });
            }
        });
    }

    loadSubscriptionData() {
        new APIClient(API_URL + 'agency/subscription/').call('GET').then((data) => {
            if (data.status === 'ERROR_NO_AGENCY') {
                alert(data['error_message']);
            } else if (data.status === 'OK') {
                this.setState({
                    subscriptionData: data.data,
                    loading: false
                });
            }
        });
    }

    loadBillingHistory() {
        this.setState({ billingLoading: true });
        new APIClient(API_URL + 'agency/subscription/billing-history/').call('GET').then((data) => {
            if (data.status === 'OK') {
                this.setState({
                    billingData: data.data,
                    billingLoading: false
                });
            } else {
                this.setState({
                    billingData: { available: false, reason: 'tesla_error' },
                    billingLoading: false
                });
            }
        }).catch(() => {
            this.setState({
                billingData: { available: false, reason: 'tesla_error' },
                billingLoading: false
            });
        });
    }

    isPendingPaymentConfirmation() {
        if (this.state.pendingPaymentUrl) return true;
        const { billingData, billingLoading } = this.state;
        if (billingLoading || !billingData || !billingData.available) return false;
        return billingData.client?.status === 'Nuevo a confirmar';
    }

    getPaymentUrl() {
        return this.state.pendingPaymentUrl
            || this.state.billingData?.client?.subscription_url
            || null;
    }

    isExpired() {
        const { subscriptionData } = this.state;
        if (!subscriptionData) return false;
        if (!subscriptionData.subscription_valid_till) return false;
        const today = new Date();
        const validTill = new Date(subscriptionData.subscription_valid_till + 'T23:59:59');
        return today > validTill;
    }

    render() {
        return (
            <section className="section-content agency-page agency-subscription-page">
                <AgencyHeader />
                <AgencyTabs activeTab="-last" />
                <div className="section-body h-items" style={{ paddingBottom: '0em' }}>
                    {this.renderContent()}
                </div>
            </section>
        );
    }

    renderContent() {
        const { t } = this.props;

        if (this.state.loading) {
            return (
                <div className="box-loader">
                    <Spinner color="positive" />
                </div>
            );
        }

        const { subscriptionData, billingData, billingLoading, isSuperAdmin, checkoutData } = this.state;
        const isFreeTrial = subscriptionData.is_free_trial;
        const isExpired = this.isExpired();
        const showPendingBanner = this.isPendingPaymentConfirmation();

        // Si está en modo checkout, mostrar el formulario
        if (checkoutData) {
            return (
                <div className="section-body-item -wider">
                    <CheckoutForm
                        checkoutData={checkoutData}
                        onBack={() => this.setState({ checkoutData: null })}
                        onCheckoutComplete={(subscriptionUrl, hasComment) => {
                            this.setState({
                                checkoutData: null,
                                pendingPaymentUrl: subscriptionUrl || null,
                                commentSent: !!hasComment,
                            });
                            this.loadSubscriptionData();
                            this.loadBillingHistory();
                        }}
                    />
                </div>
            );
        }

        return (
            <div className="section-body-item -wider">
                {this.state.commentSent && (
                    <div style={{
                        backgroundColor: '#eef7f6',
                        border: '1px solid #c8e6e3',
                        borderRadius: '0.5rem',
                        padding: '1rem 1.25rem',
                        marginBottom: '1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75em',
                    }}>
                        <i className="fa fa-check-circle" style={{ color: '#259D92', fontSize: '1.3rem' }} />
                        <div>
                            <strong style={{ fontSize: '0.95rem' }}>{t('checkout.comment_sent_title')}</strong>
                            <div style={{ fontSize: '0.85rem', color: '#4A4A4A', marginTop: '0.15em' }}>
                                {t('checkout.comment_sent_description')}
                            </div>
                        </div>
                    </div>
                )}
                {showPendingBanner && !this.state.commentSent && (
                    <PendingPaymentBanner subscriptionUrl={this.getPaymentUrl()} />
                )}

                <PlanChangeSection
                    currentTier={subscriptionData.plan_tier}
                    lookerCcEnabled={subscriptionData.looker_cc_enabled}
                    isFreeTrial={isFreeTrial}
                    isExpired={isExpired}
                    subscriptionData={subscriptionData}
                    onStartCheckout={(data) => this.setState({ checkoutData: data })}
                />

                {!isFreeTrial && (
                    <BillingHistorySection
                        billingData={billingLoading ? null : billingData}
                        onRetry={() => this.loadBillingHistory()}
                        showPaymentDetails={isSuperAdmin}
                        onEmailsUpdated={(updatedEmails) => {
                            const bd = { ...this.state.billingData };
                            if (bd.client) {
                                bd.client = { ...bd.client, ...updatedEmails };
                            }
                            this.setState({ billingData: bd });
                        }}
                        onLiquidationUpdated={() => this.loadBillingHistory()}
                    />
                )}

                <CancelSubscriptionSection
                    isFreeTrial={subscriptionData.is_free_trial}
                />
            </div>
        );
    }
}

export default withTranslation()(AgencySubscriptionContent);
